import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Decorative } from "@faire/design-tokens";
import { FakeAnchor } from "@faire/retailer-visitor-shared/components/FakeAnchor";
import { FooterSEOContent } from "@faire/retailer-visitor-shared/components/Footer/FooterSEOContent";
import { GeographicMap } from "@faire/retailer-visitor-shared/components/GeographicMap/index";
import { HeaderItemCss } from "@faire/retailer-visitor-shared/components/Header/styled";
import { AccessibilityLink } from "@faire/retailer-visitor-shared/lib/AccessibilityLink";
import { home } from "@faire/retailer-visitor-shared/paths/index";
import { useMarketStore } from "@faire/retailer-visitor-shared/stores/domain/Events/FaireMarkets/MarketStore";
import { BrandsLandingPageBottomSection } from "@faire/retailer-visitor-shared/views/brands/landing-page/BrandsLandingPageBottomSection";
import { BrandsLandingPageCaseStudies } from "@faire/retailer-visitor-shared/views/brands/landing-page/BrandsLandingPageCaseStudies";
import { BrandsLandingPageFaireMarket } from "@faire/retailer-visitor-shared/views/brands/landing-page/BrandsLandingPageFaireMarket";
import { BrandsLandingPageFaireMarketBanner } from "@faire/retailer-visitor-shared/views/brands/landing-page/BrandsLandingPageFaireMarketBanner";
import { BrandsLandingPageFaireMarketShowcase } from "@faire/retailer-visitor-shared/views/brands/landing-page/BrandsLandingPageFaireMarketShowcase";
import { BrandsLandingPageIntro } from "@faire/retailer-visitor-shared/views/brands/landing-page/BrandsLandingPageImprovementsIntro";
import { BrandsLandingPageNav } from "@faire/retailer-visitor-shared/views/brands/landing-page/BrandsLandingPageNav";
import { BrandsLandingPageTestimonials } from "@faire/retailer-visitor-shared/views/brands/landing-page/BrandsLandingPageTestimonials";
import { BrandsLandingPageTestimonialsSwipe } from "@faire/retailer-visitor-shared/views/brands/landing-page/BrandsLandingPageTestimonialsSwipe";
import { BrandsLandingPageTopSection } from "@faire/retailer-visitor-shared/views/brands/landing-page/BrandsLandingPageTopSection";
import { BRANDS_LANDING_PAGE_BELOW_HEADER_IMAGE, getIsShowingCaseStudies } from "@faire/retailer-visitor-shared/views/brands/landing-page/const";
import { useBrandsLandingPageState } from "@faire/retailer-visitor-shared/views/brands/landing-page/hooks/useBrandsLandingPageState";
import { getIntros } from "@faire/retailer-visitor-shared/views/brands/landing-page/intros";
import { BrandsLandingPageProvider } from "@faire/retailer-visitor-shared/views/brands/landing-page/stores/BrandsLandingPageContextStore";
import { getWindow, getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { withQueryParams } from "@faire/web--source/common/query-params/withQueryParams";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Column } from "@faire/web--source/slate/Layout";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { FaireLogo } from "@faire/web--source/slate/logos/FaireLogo";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useIntersectionObserver } from "@faire/web--source/ui/hooks/useIntersectionObserver";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useRef } from "react";
import { withRouter } from "react-router-dom";
import styled from "styled-components";
import { MakerSignUpFormContextStoreProvider } from "../../apply/MakerApplication/MakerSignUpFormStore";
import { MakerSignUpContextStoreProvider } from "../../apply/MakerApplication/MakerSignUpStore";
var FAIRE_MARKET_SECTION = "faire-market-section";
var BrandsLandingPageBase = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _useBrandsLandingPageState = useBrandsLandingPageState(), showBanner = _useBrandsLandingPageState.showBanner, setBanner = _useBrandsLandingPageState.setBanner, init = _useBrandsLandingPageState.init, setIsMarketSectionVisible = _useBrandsLandingPageState.setIsMarketSectionVisible, isLoading = _useBrandsLandingPageState.isLoading, goToForm = _useBrandsLandingPageState.goToForm, isPersonalized = _useBrandsLandingPageState.isPersonalized, calendlyLink = _useBrandsLandingPageState.calendlyLink;
    var _useMarketStore = useMarketStore([
        "showBrandLandingPage"
    ]), displayFaireMarket = _useMarketStore.showBrandLandingPage;
    var categoryInclusiveReference = useRef(null);
    var onScrollHandler = useCallback(function() {
        if (categoryInclusiveReference.current) {
            var _categoryInclusiveReference_current = categoryInclusiveReference.current, offsetTop = _categoryInclusiveReference_current.offsetTop, clientHeight = _categoryInclusiveReference_current.clientHeight;
            var isBelowSignup = getWindowOrThrow().scrollY > offsetTop + clientHeight;
            if (showBanner !== isBelowSignup) {
                setBanner(isBelowSignup);
            }
        }
    }, [
        categoryInclusiveReference,
        showBanner,
        setBanner
    ]);
    useEffect(function() {
        var _getWindow;
        (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.addEventListener("scroll", onScrollHandler);
        init();
        return function() {
            var _getWindow;
            (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.removeEventListener("scroll", onScrollHandler);
        };
    }, [
        init,
        onScrollHandler
    ]);
    var isSSR = useIsSSR();
    var ref = useIntersectionObserver(function(entry) {
        setIsMarketSectionVisible(entry.isIntersecting);
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsx(Column, {
            align: "center",
            children: /*#__PURE__*/ _jsx(LoadingSpinner, {
                size: "large"
            })
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            displayFaireMarket && !showBanner ? /*#__PURE__*/ _jsx(BrandsLandingPageFaireMarketBanner, {
                faireMarketSectionId: FAIRE_MARKET_SECTION
            }) : null,
            /*#__PURE__*/ _jsx(StyledHeaderWrapper, {
                children: /*#__PURE__*/ _jsxs(AccessibilityLink, {
                    to: home(),
                    id: "faire-logo-link",
                    "aria-label": strictLocalize({
                        id: "CA6lm2",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Go to Faire homepage"
                            }
                        ]
                    }),
                    children: [
                        /*#__PURE__*/ _jsx(Spacer, {
                            width: "2x"
                        }),
                        /*#__PURE__*/ _jsx(FaireLogo, {
                            inverse: true
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(BrandsLandingPageNav, {}),
            /*#__PURE__*/ _jsxs(Container, {
                children: [
                    /*#__PURE__*/ _jsx(BrandsLandingPageTopSection, {
                        categoryInclusiveReference: categoryInclusiveReference
                    }),
                    /*#__PURE__*/ _jsx(ResponsiveContentV2, {
                        mobile: /*#__PURE__*/ _jsx(MobileBannerImage, {
                            src: optimizeImageUrl({
                                url: BRANDS_LANDING_PAGE_BELOW_HEADER_IMAGE
                            }, isSSR)
                        })
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: {
                            mobileAndAbove: "3x",
                            desktopAndAbove: 96
                        }
                    }),
                    getIntros(isSSR, strictLocalize).map(function(intro) {
                        return /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx(BrandsLandingPageIntro, {
                                    index: intro.index,
                                    header: intro.header,
                                    description: intro.description,
                                    richContent: intro.richContent,
                                    reverse: intro.reverse,
                                    startTracking: showBanner,
                                    eventKey: intro.eventKey
                                }),
                                /*#__PURE__*/ _jsx(Spacer, {
                                    height: {
                                        mobileAndAbove: "3x",
                                        tabletAndAbove: "6x"
                                    }
                                })
                            ]
                        }, intro.index);
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: {
                            mobileAndAbove: "1x",
                            tabletAndAbove: "4x",
                            desktopAndAbove: 96
                        }
                    }),
                    displayFaireMarket ? /*#__PURE__*/ _jsxs("div", {
                        ref: ref,
                        style: {
                            width: "100%"
                        },
                        children: [
                            /*#__PURE__*/ _jsx(FakeAnchor, {
                                id: FAIRE_MARKET_SECTION
                            }),
                            /*#__PURE__*/ _jsx(BrandsLandingPageFaireMarketShowcase, {}),
                            /*#__PURE__*/ _jsx(BrandsLandingPageFaireMarket, {
                                onClick: goToForm
                            })
                        ]
                    }) : null,
                    getIsShowingCaseStudies() ? /*#__PURE__*/ _jsx(BrandsLandingPageCaseStudies, {}) : null,
                    /*#__PURE__*/ _jsx(ResponsiveContentV2, {
                        mobile: /*#__PURE__*/ _jsx(BrandsLandingPageTestimonialsSwipe, {})
                    }),
                    /*#__PURE__*/ _jsx(GeographicMap, {
                        showCaseStudies: getIsShowingCaseStudies()
                    }),
                    /*#__PURE__*/ _jsx(BrandsLandingPageTestimonials, {
                        isPersonalized: isPersonalized
                    }),
                    /*#__PURE__*/ _jsx(BrandsLandingPageBottomSection, {
                        onButtonClick: goToForm,
                        isPersonalized: isPersonalized,
                        calendlyLink: calendlyLink
                    }),
                    /*#__PURE__*/ _jsx(FooterSEOContent, {})
                ]
            })
        ]
    });
};
var BaseBrandsLandingPage = function(props) {
    var _props_queryParams_get;
    return /*#__PURE__*/ _jsx(MakerSignUpContextStoreProvider, {
        children: /*#__PURE__*/ _jsx(MakerSignUpFormContextStoreProvider, {
            children: /*#__PURE__*/ _jsx(BrandsLandingPageProvider, {
                value: {
                    brandToken: props.match.params.brandToken,
                    existingEmail: (_props_queryParams_get = props.queryParams.get("email_address")) !== null && _props_queryParams_get !== void 0 ? _props_queryParams_get : undefined
                },
                children: /*#__PURE__*/ _jsx(BrandsLandingPageBase, _object_spread({}, props))
            })
        })
    });
};
export var BrandsLandingPage = withRouter(withQueryParams(observer(BaseBrandsLandingPage)));
var Container = styled.div.withConfig({
    componentId: "sc-74d72295-0"
})([
    "width:100%;display:flex;flex-direction:column;margin:0 auto;align-items:center;position:relative;overflow-x:hidden;"
]);
var MobileBannerImage = styled.img.withConfig({
    componentId: "sc-74d72295-1"
})([
    "height:112px;width:100%;object-fit:cover;object-position:center;"
]);
var StyledHeaderWrapper = styled.div.withConfig({
    componentId: "sc-74d72295-2"
})([
    "background-color:",
    ";",
    ";padding:",
    ";justify-content:start;",
    "{justify-content:center;}"
], Decorative.shade.yellow, HeaderItemCss, getSpacing(0, 0, 0, "3x"), responsiveMediaQueries.mobile);
